import React from 'react'
import Input from '../Input'
import Error from './Error'
import Button from '../Button'
import CheckboxSingle from '../../components/book/steps/components/CheckboxSingle'
import PhoneNumberInput from '../../components/phone-number-input'
import { gsColors } from '../../styles/Variables'
import { FieldSubtext } from './styles'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { validateEmail } from '../../helpers/final-form-input-components'
import { useAuthContext } from './reducer'

/*
 * SignupForm — "Let's set up your account".
 *
 * Renders First Name, Last Name, and ONLY the identifier still missing after the user
 * verified their identity. No password field. Three variants:
 *   - phone-verified (otpChannel 'phone', no providerSignup): collect Email; phone held in state.
 *   - email-verified (otpChannel 'email', no providerSignup): collect Mobile number; email held in state.
 *   - provider (providerSignup set): First/Last/email prefilled, collect Mobile number; email held in state.
 * The held identifier stays in state (not rendered) and is submitted with the form.
 *
 * The provider variant always has an email: handleProviderAuth only enters it when the
 * provider proof carried one, because Starbase pins the new account to the address inside
 * that proof and would reject any other. So there is no "provider without an email" variant.
 */
export const SignupForm = ({
  handleInput,
  handleOnFocus,
  handleOnBlur,
  termsAccepted,
  toggleTermsAccepted,
  handleSignUp,
  handleProviderSignUp,
}) => {
  const { state } = useAuthContext()
  const { working, otpChannel } = state
  const isProvider = !!state.providerSignup
  const collectPhone = isProvider || otpChannel === 'email'
  const collectEmail = !collectPhone

  const handlePhoneChange = (value) => {
    handleInput({
      target: {
        name: 'phone',
        value: value || ''
      }
    })
  }

  const isDisabled = working ||
    !termsAccepted ||
    !state.nameFirst.value ||
    !state.nameLast.value ||
    (collectPhone && !isValidPhoneNumber(state.phone.value)) ||
    (collectEmail && (!state.email.value || !!validateEmail(state.email.value)))

  const submit = isProvider ? handleProviderSignUp : handleSignUp

  // real form submission, so Enter works after the browser autofills the profile fields
  const handleFormSubmit = (event) => {
    event.preventDefault()
    if (isDisabled) {
      return
    }
    submit()
  }

  return (
    <form
      id='sign-up-form'
      name='sign-up-form'
      autoComplete='on'
      noValidate
      onSubmit={handleFormSubmit}
    >
      <div className="field-container">
        <Input
          className='name-first'
          placeholder='First Name'
          type='text'
          name='nameFirst'
          value={state.nameFirst.value}
          onChange={handleInput}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          autoComplete='given-name'
          autoCapitalize='words'
          autoCorrect='off'
          spellCheck={false}
        />
      </div>
      <div className="field-container">
        <Input
          className='name-last'
          placeholder='Last Name'
          type='text'
          name='nameLast'
          value={state.nameLast.value}
          onChange={handleInput}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          autoComplete='family-name'
          autoCapitalize='words'
          autoCorrect='off'
          spellCheck={false}
        />
      </div>
      {collectPhone && (
        <div
          className={`field-container phone-input-container ${state.phone.error ? 'phone-error' : ''}`}
        >
          <PhoneNumberInput
            value={state.phone.value}
            onChange={handlePhoneChange}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            autoComplete='tel'
            name='phone'
            state={state.phone}
          />

          {state.phone.error && <p className='error-message'>Please enter a valid phone number</p>}
          <FieldSubtext>
            We&rsquo;ll send you appointment updates and let you know when your pro is on the way.
          </FieldSubtext>
        </div>
      )}
      {collectEmail && (
        <div className="field-container email-container">
          <Input
            className={`email-address ${state.email.error ? 'input-error' : ''}`}
            placeholder='Email Address'
            type='email'
            name='email'
            value={state.email.value}
            onChange={handleInput}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            autoComplete='email'
            inputMode='email'
            autoCapitalize='none'
            autoCorrect='off'
            spellCheck={false}
          />
          {state.email.error && <p className='error-message'>Please enter a valid email</p>}
        </div>
      )}
      <div className="field-container terms-container">
        <CheckboxSingle
          name="terms"
          onChange={toggleTermsAccepted}
          textComponent={CheckboxText}
          value={termsAccepted}
          cobaltChecked
        />
      </div>
      <Error error={state.submitError} />
      <div className="field-container">
        <Button margin="10px 0 0" large fill='true'
          id="submit-btn"
          type="submit"
          color={gsColors.cobalt}
          disabled={isDisabled}
          working={working}
        >
          Create Account
        </Button>
      </div>
    </form>
  )
}

const CheckboxText = () => {
  return (
    <span>
      I agree to Glamsquad&rsquo;s
      <a href="/terms" target="_blank">Terms of Service</a> and <a href="/privacypolicy" target="_blank">Privacy Policy</a>
    </span>
  )
}
